import React from 'react';

import noop from 'lodash/noop';

import {TThunkCreator} from '@edna/models/apiModel';

import useEvent from './useEvent';
import useOnMount from './useOnMount';

export type TOptions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch?: any;
  scrollRef?: React.RefObject<HTMLElement>;
  hasMore?: boolean;
  reserve?: number;
  setHasScroll?: (hasScroll: boolean) => void;
  loadMore?: TThunkCreator;
  disableInitialLoad?: boolean;
};

const calcRemainedScroll = (element: HTMLElement) =>
  element.scrollHeight - element.scrollTop - element.clientHeight;

const useLoadByScroll = ({
  watch,
  scrollRef,
  hasMore: active,
  reserve = 400,
  setHasScroll = noop,
  loadMore = noop,
  disableInitialLoad = false,
}: TOptions) => {
  const handleScroll = React.useCallback(() => {
    const element = scrollRef?.current ?? document.documentElement;

    setHasScroll(element.scrollTop > 0);

    if (active) {
      if (calcRemainedScroll(element) < reserve) {
        loadMore();
      }
    }
  }, [active, loadMore, reserve, setHasScroll, scrollRef]);

  const scrollNode = scrollRef?.current ?? document;

  useOnMount(() => {
    if (!disableInitialLoad) {
      loadMore();
    }
  });

  React.useEffect(() => {
    setTimeout(handleScroll, 0);
  }, [watch, handleScroll]);

  useEvent('scroll', handleScroll, {
    target: scrollNode,
    listenerOptions: {passive: true},
  });
};

export default useLoadByScroll;
